import { Key } from '@/enums/key';
import { KeyDescription } from '@/enums/key_description';
import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import { assert_false } from '@shared/asserts/utils_asserts';
import { EShortcutGroup as EGroup } from '@shared/dto/hint_shortcuts/e_shortcut_group';
import { EShortcutType as EType } from '@shared/dto/hint_shortcuts/e_shortcut_type';
import { IHintShortcutData } from '@shared/dto/hint_shortcuts/i_hint_shortcut_data';
import { IHintKeyboardDataRow } from '@shared/dto/hint_shortcuts/i_hint_shortcut_data_row';
import { IMouseData } from '@shared/dto/hint_shortcuts/i_mouse_data';
import { DataHintsShortcutMap } from '@shared/types/types';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { use_hint_shortcut_change_cells_size_with_scroll } from './use_hint_shortcut_change_cells_size_with_scroll';
import { use_hints_shortcut_paste_image } from './use_hints_shortcut_paste_image';
import { use_hints_shortcut_visibility } from './use_hints_shortcut_visibility';
import { use_hints_shortcut_workspace_navigation } from './use_hints_shortcut_workspace_navigation';

type Translator = TFunction<string, undefined>;

let MOUSE_DATA_DEFAULT: IMouseData = {
  is_mouse_left: false,
  is_mouse_left_double_click: false,
  is_mouse_right: false,
  is_scroll_up: false,
  is_scroll_down: false,
};

/*
 * HELPER FUNCTIONS
 */

export function create_data_row(text: string, keys: string[], mouse_data: IMouseData = MOUSE_DATA_DEFAULT): IHintKeyboardDataRow {
  let out: IHintKeyboardDataRow = { text, keys, mouse_data };

  return out;
}

function add_hint_multi_rows(
  map: DataHintsShortcutMap,
  key: EType,
  group: EGroup,
  data_rows: IHintKeyboardDataRow[],
  keys_have_same_animation_delay: boolean = true,
) {
  let item: IHintShortcutData = { data_rows, key, keys_have_same_animation_delay, group };

  assert_false(map.has(key), { assert_type: ENUM__error_type.VALIDATION_ITEM_ALREADY_EXISTS, key });

  map.set(key, item);
}

function add_hint_1_row(
  map: DataHintsShortcutMap,
  key: EType,
  group: EGroup,
  text: string,
  keyboard_keys: string[],
  mouse_data: IMouseData = MOUSE_DATA_DEFAULT,
) {
  add_hint_multi_rows(map, key, group, [create_data_row(text, keyboard_keys, mouse_data)]);
}

/*
 * MAP
 */

function create_map_hints_data(t: Translator) {
  let out: DataHintsShortcutMap = new Map();

  /*
   * COLLECTIONS
   */

  add_hint_1_row(out, EType.COLLECTIONS_CREATE, EGroup.COLLECTIONS, t(i18n_base_ui.shortcut_hints.collections.collections_create), [Key.C]);

  /*
   * GENERATION
   */

  add_hint_1_row(out, EType.GENERATION_NAME_EDIT_START, EGroup.GENERATION, t(i18n_base_ui.shortcut_hints.generation.generation_name_edit_start), [], {
    is_mouse_left: true,
  });
  add_hint_1_row(out, EType.GENERATION_START, EGroup.GENERATION, t(i18n_base_ui.shortcut_hints.generation.generation_start), [Key.Enter]);
  add_hint_1_row(out, EType.GENERATION_START_WITH_NEW_SEEDS, EGroup.GENERATION, t(i18n_base_ui.shortcut_hints.generation.generation_start_with_new_seeds), [
    Key.Shift,
    Key.Enter,
  ]);
  add_hint_1_row(out, EType.GENERATION_COMMIT, EGroup.GENERATION, 'Commit', [Key.Enter]);
  add_hint_1_row(out, EType.GENERATION_CONTINUE, EGroup.GENERATION, t(i18n_base_ui.shortcut_hints.generation.generation_continue), [Key.Enter]);
  add_hint_1_row(out, EType.GENERATION_STOP, EGroup.GENERATION, t(i18n_base_ui.shortcut_hints.generation.generation_stop), [Key.Escape]);

  /*
   * GRID2D
   */

  add_hint_1_row(out, EType.GRID2D_BAR_DECREASE_CELL_SIZE, EGroup.GRID2D_BAR, t(i18n_base_ui.shortcut_hints.grid2d.bar_decrease_cell_size), [Key.Shift], {
    is_mouse_left: true,
  });

  add_hint_1_row(
    out,
    EType.GRID2D_CELL_BUTTON_COPY_TO_CLIPBOARD,
    EGroup.GRID2D_CELL,
    t(i18n_base_ui.shortcut_hints.grid2d.cell_button_copy_to_clipboard),
    [Key.Shift],
    { is_mouse_left: true },
  );
  add_hint_1_row(
    out,
    EType.GRID2D_CELL_OPEN_FULLSCREEN_IMAGE,
    EGroup.GRID2D_CELL,
    t(i18n_base_ui.shortcut_hints.grid2d.cell_open_fullscreen_image),
    [Key.Shift],
    {
      is_mouse_left: true,
    },
  );

  /*
   * INPUT
   */
  add_hint_multi_rows(out, EType.INPUT_WITH_BUTTON_EDIT_END, EGroup.INPUT, [
    create_data_row(t(i18n_base_ui.shortcut_hints.input.with_button_edit_end_save), [Key.Enter]),
    create_data_row(t(i18n_base_ui.shortcut_hints.input.with_button_edit_end_dismiss), [Key.Escape]),
  ]);

  /*
   * NAVIGATION
   */
  add_hint_multi_rows(
    out,
    EType.NAVIGATION_HOTKEYS_BETWEEN_PAGES,
    EGroup.NAVIGATION,
    [
      create_data_row(t(i18n_base_ui.shortcut_hints.navigation.hotkeys_between_pages_search), [Key.Q]),
      create_data_row(t(i18n_base_ui.shortcut_hints.navigation.hotkeys_between_pages_generation), [Key.W]),
      create_data_row(t(i18n_base_ui.shortcut_hints.navigation.hotkeys_between_pages_plan), [Key.E]),
      create_data_row(t(i18n_base_ui.shortcut_hints.navigation.hotkeys_between_pages_collections), [Key.R]),
      create_data_row(t(i18n_base_ui.shortcut_hints.navigation.hotkeys_between_pages_models), [Key.T]),
    ],
    false,
  );

  /*
   *  FULLSCREEN GRID2D
   */

  add_hint_1_row(out, EType.FULLSCREEN_GRID2D_CLOSE, EGroup.FULLSCREEN_GRID2D, t(i18n_base_ui.shortcut_hints.fullscreen_grid2d.grid2d_close), [Key.Escape]);

  /*
   *  FULLSCREEN IMAGE
   */

  add_hint_multi_rows(out, EType.FULLSCREEN_GRID2D_IMAGE_CLOSE, EGroup.FULLSCREEN_GRID2D_IMAGE, [
    create_data_row(t(i18n_base_ui.shortcut_hints.fullscreen_image.image_close), [Key.Shift, Key.Escape]),
    create_data_row(t(i18n_base_ui.shortcut_hints.fullscreen_image.image_close), [Key.Shift], { is_mouse_left: true }),
    create_data_row(t(i18n_base_ui.shortcut_hints.fullscreen_image.show_grid2d), [Key.Escape]),
  ]);
  add_hint_multi_rows(out, EType.FULLSCREEN_GRID2D_IMAGE_NAVIGATION_BY_KEYS, EGroup.FULLSCREEN_GRID2D_IMAGE, [
    create_data_row(t(i18n_base_ui.shortcut_hints.fullscreen_image.navigation_previous), [KeyDescription.ArrowLeft]),
    create_data_row(t(i18n_base_ui.shortcut_hints.fullscreen_image.navigation_next), [KeyDescription.ArrowRight]),
  ]);

  add_hint_multi_rows(out, EType.FULLSCREEN_GRID2D_IMAGE_NAVIGATION_BY_SCROLL, EGroup.FULLSCREEN_GRID2D_IMAGE, [
    create_data_row(t(i18n_base_ui.shortcut_hints.fullscreen_image.navigation_previous), [], { is_scroll_up: true }),
    create_data_row(t(i18n_base_ui.shortcut_hints.fullscreen_image.navigation_next), [], { is_scroll_down: true }),
  ]);

  /*
   *  FULLSCREEN IMAGE CANVAS
   */

  add_hint_1_row(out, EType.FULLSCREEN_IMAGE_CANVAS_CLOSE, EGroup.FULLSCREEN_IMAGE_CANVAS, t(i18n_base_ui.shortcut_hints.fullscreen_image_canvas.image_close), [
    Key.Escape,
  ]);
  add_hint_1_row(
    out,
    EType.FULLSCREEN_IMAGE_CANVAS_CROP_BOX_MAXIMIZE,
    EGroup.FULLSCREEN_IMAGE_CANVAS,
    t(i18n_base_ui.shortcut_hints.fullscreen_image_canvas.crop_box_maximize),
    [],
    { is_mouse_left_double_click: true },
  );

  /*
   * SLIDER
   */

  add_hint_1_row(out, EType.SLIDER_DECREASE_STEP, EGroup.SLIDER, t(i18n_base_ui.shortcut_hints.slider.decrease_step), [Key.Shift], { is_mouse_left: true });

  /*
   * USER TASK HISTORY
   */

  add_hint_1_row(out, EType.USER_TASK_CREATE, EGroup.USER_TASK, t(i18n_base_ui.shortcut_hints.user_task_history.create_new), [Key.C]);

  return out;
}

export function use_hints_shortcuts_data(hints_shortcut_visible: EType[]) {
  const { t } = useTranslation(ENUM__i18n_namespace.BASE_UI);

  let DATA_HINTS_SHORTCUTS_MAP: DataHintsShortcutMap = create_map_hints_data(t);

  const workspace_navigation = use_hints_shortcut_workspace_navigation();
  const paste_image = use_hints_shortcut_paste_image();
  const hints_visitibility = use_hints_shortcut_visibility();
  const change_cells_size_with_scroll = use_hint_shortcut_change_cells_size_with_scroll();

  const hint_shortcuts_data = useMemo(() => hints_shortcut_visible.filter(hint_key => hint_key !== EType.STUB).map(get_hint_data), [hints_shortcut_visible]);
  /*
   * EFFECTS
   */

  /*
   * METHODS
   */

  function get_hint_data(key: EType) {
    let out: IHintShortcutData;

    switch (key) {
      case EType.GRID2D_CHANGE_CELL_SIZE_WITH_SCROLL:
        out = change_cells_size_with_scroll;
        break;
      case EType.SHORTCUT_HINTS_VISIBILITY:
        out = hints_visitibility;
        break;
      case EType.NAVIGATION_HOTKEYS_BETWEEN_WORKSPACES:
        out = workspace_navigation;
        break;
      case EType.GENERATION_SETTINGS_IMAGE_CANVAS_PASTE_IMAGE:
        out = paste_image;
        break;
      default:
        out = DATA_HINTS_SHORTCUTS_MAP.get_or_assert(key);
        break;
    }

    return out;
  }

  return { hint_shortcuts_data };
}
